import type {FileSummary} from 'folder-items/FileConstants';
import type {OmniboxResponse} from 'omnibox/omniboxTypes';
import * as Net from 'util/Net';

export interface ProjectFolderTypeaheadItem {
  kind: 'project' | 'folder';
  apiId: string;
  name: string;
  projectName?: string;
}

export interface UserTypeaheadItem {
  apiId: string;
  name: string;
  handle: string;
  isSelf: boolean;
}

function buildOmniboxRequestBody(data: {
  query: string;
  requestId?: string;
  activeWorkspaceTabId?: string;
  openWorkspaceTabIds?: Array<string>;
  schemaApiIds?: Array<string>;
  objectTypes?: Array<string>;
  projectApiIds?: Array<string>;
  folderApiIds?: Array<string>;
  authorApiIds?: Array<string>;
}): object {
  return {
    ...data,
    // intentionally en-US to match the rest of our prompting
    userLocalTime: new Date().toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    }),
    userTimezone: new Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

/**
 * Stream omnibox results via Ably. Makes a regular POST request that publishes
 * partial results to the user's Ably channel as they become available.
 * Returns a promise that resolves with the final complete results.
 */
export function streamOmniboxQuery(data: {
  query: string;
  requestId: string;
  activeWorkspaceTabId?: string;
  openWorkspaceTabIds?: Array<string>;
  schemaApiIds?: Array<string>;
  objectTypes?: Array<string>;
  projectApiIds?: Array<string>;
  folderApiIds?: Array<string>;
  authorApiIds?: Array<string>;
}): Promise<OmniboxResponse> {
  return Net.postJSONPromise({
    url: '/1/api/omnibox/stream',
    data: buildOmniboxRequestBody(data),
  });
}

export function fetchProjectFolderTypeahead(): Promise<Array<ProjectFolderTypeaheadItem>> {
  return Net.getJSONPromise('/1/api/omnibox/project-folder-typeahead');
}

export function fetchUserTypeahead(): Promise<Array<UserTypeaheadItem>> {
  return Net.getJSONPromise('/1/api/omnibox/user-typeahead');
}

export function fetchRecentItems(): Promise<{data: Array<FileSummary>}> {
  return Net.getJSONPromise('/1/api/users/me/recent/');
}

export interface FilteredRecentItem {
  apiId: string;
  name: string;
  objectType: string;
  modifiedAt?: string | null;
}

export function fetchFilteredRecent(data: {
  schemaApiIds?: Array<string>;
  objectTypes?: Array<string>;
  projectApiIds?: Array<string>;
  folderApiIds?: Array<string>;
  authorApiIds?: Array<string>;
}): Promise<Array<FilteredRecentItem>> {
  return Net.postJSONPromise({
    url: '/1/api/omnibox/filtered-recent',
    data,
  });
}
